import React, { useEffect, useRef, useState } from "react";
import { NavLink, useNavigate, Link } from "react-router-dom";
import {
  Home, Search, Library, Crown, Radar, Upload, UserCircle, Users,
  Settings as SettingsIcon, ShieldCheck, Sparkles, Megaphone, LogIn, LogOut,
  Download, ChevronDown, Menu, X, Flame,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import SmartImage from "@/components/SmartImage";

/* ──────────────────────────────────────────────────────────────────────
 * useInstallPrompt — captures the PWA ``beforeinstallprompt`` event so we
 * can show our own "Install App" button instead of relying on the browser
 * chrome's tiny address-bar icon.
 *
 *   const { canInstall, install, installed } = useInstallPrompt();
 *
 * canInstall — true once the browser fires beforeinstallprompt
 * install()  — triggers the native prompt
 * installed  — true after the user accepts (also hides the button)
 * ────────────────────────────────────────────────────────────────────── */
function useInstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    // If already running as an installed PWA, hide the button entirely.
    const standalone =
      window.matchMedia?.("(display-mode: standalone)").matches ||
      window.navigator?.standalone;
    if (standalone) setInstalled(true);

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferred(null);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const install = async () => {
    if (!deferred) return false;
    try {
      deferred.prompt();
      const choice = await deferred.userChoice;
      if (choice?.outcome === "accepted") setInstalled(true);
      setDeferred(null);
      return choice?.outcome === "accepted";
    } catch {
      return false;
    }
  };

  return { canInstall: !!deferred && !installed, install, installed };
}

const TopNavLink = ({ to, icon: Icon, label, testId }) => (
  <NavLink
    to={to}
    data-testid={testId}
    end={to === "/"}
    className={({ isActive }) =>
      `inline-flex items-center gap-2 px-3 py-2 rounded-sm text-[13px] font-bold uppercase tracking-[0.06em] transition-all whitespace-nowrap ${
        isActive
          ? "text-white bg-white/10"
          : "text-white/55 hover:text-white hover:bg-white/[0.06]"
      }`
    }
  >
    {Icon && <Icon size={15} strokeWidth={2.2} />}
    <span>{label}</span>
  </NavLink>
);

const UserMenu = ({ user, isAdmin, login, logout }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const onClick = (e) => {
      if (!ref.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  if (!user) {
    return (
      <button
        data-testid="header-signin-btn"
        onClick={login}
        className="inline-flex items-center gap-2 px-3 py-2 rounded-sm text-[13px] font-bold uppercase tracking-wider text-white/70 hover:text-white hover:bg-white/[0.06] transition"
      >
        <LogIn size={14} /> Sign in
      </button>
    );
  }

  const initials = (user.name || user.email || "?")
    .split(/\s+/)
    .map((p) => p[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <div ref={ref} className="relative">
      <button
        data-testid="header-user-menu-btn"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 px-2 py-1.5 rounded-full bg-white/[0.06] hover:bg-white/[0.12] border border-white/10 transition"
      >
        <SmartImage
          src={user.picture}
          alt={user.name || "You"}
          seed={user.name || user.email || "?"}
          className="w-7 h-7 rounded-full overflow-hidden"
          imgClassName="w-full h-full object-cover"
        />
        <ChevronDown size={14} className={`transition ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div
          data-testid="header-user-menu"
          className="absolute right-0 top-full mt-2 w-64 rounded-sm border border-white/10 bg-black/95 backdrop-blur-xl shadow-2xl overflow-hidden z-50"
        >
          <div className="px-4 py-3 border-b border-white/10">
            <p className="text-sm font-bold text-white truncate">{user.name}</p>
            <p className="text-xs text-white/45 truncate">{user.email}</p>
          </div>
          <Link to="/profile" data-testid="menu-profile" onClick={() => setOpen(false)} className="flex items-center gap-3 px-4 py-2.5 text-sm text-white/80 hover:text-white hover:bg-white/[0.06]">
            <UserCircle size={15} /> Profile
          </Link>
          <Link to="/premium" data-testid="menu-premium" onClick={() => setOpen(false)} className="flex items-center gap-3 px-4 py-2.5 text-sm text-white/80 hover:text-white hover:bg-white/[0.06]">
            <Crown size={15} /> Premium
          </Link>
          <Link to="/promote" data-testid="menu-promote" onClick={() => setOpen(false)} className="flex items-center gap-3 px-4 py-2.5 text-sm text-white/80 hover:text-white hover:bg-white/[0.06]">
            <Megaphone size={15} /> Promote
          </Link>
          <Link to="/settings" data-testid="menu-settings" onClick={() => setOpen(false)} className="flex items-center gap-3 px-4 py-2.5 text-sm text-white/80 hover:text-white hover:bg-white/[0.06]">
            <SettingsIcon size={15} /> Settings
          </Link>
          {isAdmin && (
            <Link to="/admin" data-testid="menu-admin" onClick={() => setOpen(false)} className="flex items-center gap-3 px-4 py-2.5 text-sm text-white/80 hover:text-white hover:bg-white/[0.06]">
              <ShieldCheck size={15} /> Admin
            </Link>
          )}
          {isAdmin && (
            <Link to="/ar" data-testid="menu-ar" onClick={() => setOpen(false)} className="flex items-center gap-3 px-4 py-2.5 text-sm text-white/80 hover:text-white hover:bg-white/[0.06]">
              <Sparkles size={15} /> A&R Dashboard
            </Link>
          )}
          <button
            data-testid="menu-signout"
            onClick={() => { setOpen(false); logout(); }}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-white/80 hover:text-white hover:bg-vermilion/15 border-t border-white/10"
          >
            <LogOut size={15} /> Sign out
          </button>
        </div>
      )}
    </div>
  );
};

const TopHeader = () => {
  const { user, login, logout } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { canInstall, install } = useInstallPrompt();

  useEffect(() => {
    if (!user) { setIsAdmin(false); return; }
    api.get("/admin/whoami").then((w) => setIsAdmin(!!w.data?.is_admin)).catch(() => setIsAdmin(false));
  }, [user]);

  return (
    <header
      data-testid="top-header"
      className="sticky top-0 z-40 bg-black/70 backdrop-blur-2xl border-b border-white/10"
    >
      <div className="max-w-[1600px] mx-auto px-4 sm:px-8 h-16 flex items-center gap-3 sm:gap-5">
        {/* Logo */}
        <Link to="/" data-testid="header-logo" className="flex items-center gap-2 shrink-0">
          <span className="font-display text-2xl font-black uppercase tracking-tight">
            <span className="text-white">1o1</span>
            <span className="text-vermilion">sound</span>
          </span>
        </Link>

        {/* Primary nav — collapses on mobile into a hamburger */}
        <nav className="hidden md:flex items-center gap-1 ml-2">
          <TopNavLink to="/" icon={Home} label="Home" testId="nav-home" />
          <TopNavLink to="/search" icon={Search} label="Search" testId="nav-search" />
          <TopNavLink to="/hotspot" icon={Radar} label="Hotspot" testId="nav-hotspot" />
          <TopNavLink to="/library" icon={Library} label="Library" testId="nav-library" />
          <TopNavLink to="/coming-soon" icon={Flame} label="Coming Soon" testId="nav-coming-soon" />
          <TopNavLink to="/upload" icon={Upload} label="Upload" testId="nav-upload" />
          <TopNavLink to="/artists" icon={Users} label="Artists" testId="nav-artists" />
        </nav>

        <div className="flex-1" />

        {/* Install App button — only visible when the browser fires
            beforeinstallprompt (Chrome/Edge/Android) and the app isn't
            already installed. Hidden on iOS Safari (no event support). */}
        {canInstall && (
          <button
            data-testid="install-app-btn"
            onClick={install}
            className="hidden sm:inline-flex items-center gap-2 bg-vermilion hover:bg-vermilion-hover text-white text-[12px] font-black uppercase tracking-wider px-4 py-2 rounded-sm transition shadow-[0_8px_24px_rgba(255,46,0,0.35)] whitespace-nowrap"
          >
            <Download size={14} /> Install App
          </button>
        )}

        {/* User menu */}
        <UserMenu user={user} isAdmin={isAdmin} login={login} logout={logout} />

        {/* Mobile hamburger */}
        <button
          data-testid="header-mobile-menu"
          onClick={() => setMobileOpen((o) => !o)}
          className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-sm border border-white/10 text-white/80 hover:text-white"
        >
          {mobileOpen ? <X size={18} /> : <Menu size={18} />}
        </button>
      </div>

      {/* Mobile dropdown nav */}
      {mobileOpen && (
        <div
          data-testid="header-mobile-nav"
          className="md:hidden border-t border-white/10 bg-black/95 backdrop-blur-xl px-4 py-3 grid grid-cols-2 gap-1"
        >
          <TopNavLink to="/" icon={Home} label="Home" testId="m-nav-home" />
          <TopNavLink to="/search" icon={Search} label="Search" testId="m-nav-search" />
          <TopNavLink to="/hotspot" icon={Radar} label="Hotspot" testId="m-nav-hotspot" />
          <TopNavLink to="/library" icon={Library} label="Library" testId="m-nav-library" />
          <TopNavLink to="/coming-soon" icon={Flame} label="Coming Soon" testId="m-nav-coming-soon" />
          <TopNavLink to="/upload" icon={Upload} label="Upload" testId="m-nav-upload" />
          <TopNavLink to="/artists" icon={Users} label="Artists" testId="m-nav-artists" />
          <TopNavLink to="/promote" icon={Megaphone} label="Promote" testId="m-nav-promote" />
          {canInstall && (
            <button
              onClick={install}
              data-testid="install-app-btn-mobile"
              className="col-span-2 mt-2 inline-flex items-center justify-center gap-2 bg-vermilion text-white text-xs font-black uppercase tracking-wider px-4 py-3 rounded-sm"
            >
              <Download size={14} /> Install 1o1sound App
            </button>
          )}
        </div>
      )}
    </header>
  );
};

export default TopHeader;
